import { getCurrentAge, getUsefullLifeRemaining } from "../../config/utils";
import { isAppliedWildcardFilterEntry } from "../../utils/wildCardFilterUtils";

export const VOICE_FILTER_KEYS = ["voice_text", "voice_text_mobile", "has_voice_text", "has_voice_text_mobile"];
export const VOICE_TRANSCRIPT_KEYS = ["voice_text", "voice_text_mobile"];
const HAS_VOICE_KEYS = ["has_voice_text", "has_voice_text_mobile"];
const VOICE_PARAM_TO_ADDON = {
    "voice_upload.voice_text": "voice_text",
    "voice_upload.voice_text_mobile": "voice_text_mobile",
    has_voice_text: "has_voice_text",
    has_voice_text_mobile: "has_voice_text_mobile"
};
export const splitVoiceFilters = ({ list, filters } = {}) => {
    const addon = {};
    const restList = list ? {} : list;
    Object.entries(list ?? {}).forEach(([key, value]) => {
        const addonKey = VOICE_PARAM_TO_ADDON[key];
        if (!addonKey) restList[key] = value;
        else if (HAS_VOICE_KEYS.includes(addonKey)) {
            const picks = [...new Set((value ?? []).map(String))];
            if (picks.length === 1) addon[addonKey] = picks[0] === "true"; // Yes + No (or nothing) = no constraint
        } else if (value?.length) addon[addonKey] = value;
    });
    const restFilters = filters ? {} : filters;
    Object.entries(filters ?? {}).forEach(([key, value]) => {
        const addonKey = VOICE_PARAM_TO_ADDON[key];
        if (!addonKey) restFilters[key] = value;
        else if (!HAS_VOICE_KEYS.includes(addonKey) && isAppliedWildcardFilterEntry(value)) {
            addon[`${addonKey}_filter`] = { filters: value.filters, key: value.key };
        }
    });
    return { list: restList, filters: restFilters, addon };
};

export const buildAddonFilters = (notRejected, voiceAddon = {}) => {
    const addon = { ...(notRejected ? { not_rejected: true } : {}), ...(voiceAddon ?? {}) };
    return Object.keys(addon).length ? { addon_filters: addon } : {};
};

export const extractFields = json => {
    const sortedBands = Object.keys(json).sort((a, b) => json?.[a]?.index - json?.[b]?.index);
    const fieldsArray = [];

    function processFields(fields) {
        (fields ?? []).forEach(field => {
            const key = Object.keys(field)[0];
            fieldsArray.push(field[key]);
        });
    }

    sortedBands.forEach(group => {
        json?.[group]?.fields && processFields(json[group].fields);
    });

    return fieldsArray;
};

export const isJsonbFilterField = field =>
    ["asset_static_fields", "asset_dynamic_fields"].includes(field?.split(".")?.[0]) || ["ai_flat_json"].includes(field?.split(".")?.[1]);

export const AI_FIELDS_BAND = "AI Fields";
const AI_ANALYZE_STATUS_FIELD = "analyze_status";

export const getAiFieldFilterKey = fieldName =>
    fieldName === AI_ANALYZE_STATUS_FIELD ? `survey_form_for_asset.${fieldName}` : `ai_analyze_images.ai_flat_json.${fieldName}`;

export const getAiFilterFields = (aiFieldsConfig = []) =>
    (Array.isArray(aiFieldsConfig) ? aiFieldsConfig : [])
        .filter(field => field?.field_name)
        .map(field => {
            const fieldName = field.field_name;
            const filterKey = getAiFieldFilterKey(fieldName);
            const isDateField = ["date", "date_time"].includes(field.field_type);
            return {
                label: field.label || fieldName,
                key: `ai_${fieldName}`,
                paramKey: filterKey,
                band: AI_FIELDS_BAND,
                getListKey: filterKey,
                searchKey: filterKey,
                // Dates go straight to the wildcard date picker (as in Additional Fields); everything else gets a
                // dropdown, with a wildcard/text search alongside it for non-boolean types
                isWildCardFilter: isDateField,
                fieldType: ["select", "custom_select"].includes(field.field_type)
                    ? "select"
                    : isDateField
                      ? "date"
                      : field.field_type === "number"
                        ? "number"
                        : field.field_type || "text",
                hasWildCardFilter: !isDateField && field.field_type !== "boolean",
                isAiField: true
            };
        });

const toIntList = value => (value?.length ? (value ?? []).map(e => parseInt(e)) : undefined);

export const buildMobileAssetFilterParams = ({ list = {}, filters = {}, order = {}, idList = {} } = {}) => {
    let restList = { ...(idList ?? {}), ...(list ?? {}) };
    let restFilters = { ...(filters ?? {}) };
    let restOrder = { ...(order ?? {}) };
    let jsonb_list;
    let jsonb_filters;
    let jsonb_order;
    let assetType;

    Object.keys(restList).forEach(field => {
        if (isJsonbFilterField(field)) {
            jsonb_list = { ...(jsonb_list ?? {}), [field]: { value: restList[field] } };
            const { [field]: omit, ...rest } = restList;
            restList = rest;
        }
    });

    let formattedList;
    Object.entries(restList).forEach(([key, value]) => {
        const field = key?.split(".")?.[0];
        const objectKey = key?.split(".")?.[1];
        if (field === "with_&_without_new_field") {
            assetType = value;
            return;
        }
        formattedList = {
            ...(formattedList ?? {}),
            ...(field === "status" || field === "survey_type" || field === "has_new_val"
                ? {
                      survey_form_for_asset: {
                          ...(formattedList?.survey_form_for_asset ?? {}),
                          [field]: field === "has_new_val" ? (value ?? [])?.map(v => v?.includes("With new field")) : toIntList(value)
                      }
                  }
                : field === "survey_status"
                  ? { [field]: { ...(formattedList?.[field] ?? {}), [objectKey]: toIntList(value) } }
                  : { [field]: { ...(formattedList?.[field] ?? {}), [objectKey]: value } })
        };
    });

    Object.keys(restFilters).forEach(field => {
        if (isJsonbFilterField(field)) {
            jsonb_filters = { ...(jsonb_filters ?? {}), [field]: restFilters[field] };
            const { [field]: omit, ...rest } = restFilters;
            restFilters = rest;
        }
    });

    Object.keys(restOrder).forEach(field => {
        if (isJsonbFilterField(field)) {
            jsonb_order = { ...(jsonb_order ?? {}), [field]: restOrder[field] };
            const { [field]: omit, ...rest } = restOrder;
            restOrder = rest;
        }
    });

    return { list: formattedList, rawList: restList, filters: restFilters, order: restOrder, jsonb_list, jsonb_filters, jsonb_order, assetType };
};

export const generateUniqueId = (existingIds, min = 1, max = 10000) => {
    let uniqueId;
    do {
        uniqueId = Math.floor(Math.random() * (max - min + 1)) + min;
    } while (existingIds?.some(e => e?.id === uniqueId));
    return uniqueId;
};

export const SERVICE_LIFE_FIELD = "assets.service_life";
export const CAPACITY_UNIT_FIELD = "assets.capacity_unit";
export const CRITICALITY_FIELD = "assets.criticality";
export const INSTALLED_YEAR_FIELD = "assets.installed_year";
export const USEFUL_LIFE_REMAINING_FIELD = "assets.usefull_life_remaining";
export const END_OF_SERVICE_LIFE_FIELD = "assets.end_of_service_life";
export const CURRENT_AGE_FIELD = "assets.current_age";

export const UNIFORMAT_AUTO_FIELD_LABELS = {
    [SERVICE_LIFE_FIELD]: "Service Life",
    [CAPACITY_UNIT_FIELD]: "Capacity Unit",
    [CRITICALITY_FIELD]: "Criticality"
};

export const isEmptyUniformatValue = value => value === "" || value === null || value === undefined;

export const getUniformatLevel = fieldName => Number(fieldName?.match(/uniformat_level_(\d+)_/)?.[1]);

export const getEndOfServiceLife = (installed_year, service_life) =>
    parseInt(installed_year) && parseInt(service_life) ? parseInt(installed_year) + parseInt(service_life) : "";

export const getUniformatAutoValues = (option, fieldName, hasField = () => true) => {
    if (!option) return {};
    const values = {};
    if (hasField(SERVICE_LIFE_FIELD) && ("service_life" in option || "asset_eul" in option))
        values[SERVICE_LIFE_FIELD] = ("service_life" in option ? option?.service_life : option?.asset_eul) ?? "";
    if (hasField(CAPACITY_UNIT_FIELD) && "asset_unit" in option) values[CAPACITY_UNIT_FIELD] = option?.asset_unit ?? "";
    if (hasField(CRITICALITY_FIELD) && getUniformatLevel(fieldName) === 6 && "average_criticality" in option)
        values[CRITICALITY_FIELD] = option?.average_criticality ?? "";
    return values;
};

export const getUniformatChanges = (values = {}, getFieldValue = () => undefined) =>
    Object.entries(values ?? {})
        .filter(([field, newValue]) => String(newValue ?? "") !== String(getFieldValue(field) ?? ""))
        .map(([field, newValue]) => ({
            field,
            label: UNIFORMAT_AUTO_FIELD_LABELS[field] ?? field,
            from: getFieldValue(field),
            to: newValue
        }));

export const getUniformatChangeSummary = (values = {}, getFieldValue = () => undefined) => {
    const changes = getUniformatChanges(values, getFieldValue);
    if (!changes?.length) return null;
    return {
        values,
        changes,
        serviceLifeChanged: changes.some(change => change?.field === SERVICE_LIFE_FIELD),
        newServiceLife: values?.[SERVICE_LIFE_FIELD],
        hasOnlyEmptyValues: changes.every(change => isEmptyUniformatValue(change?.to))
    };
};

export const getUniformatValuesWithDerivedFields = (values = {}, getFieldValue = () => undefined) => {
    if (!Object.keys(values ?? {}).length) return {};
    const installedYear = getFieldValue(INSTALLED_YEAR_FIELD);
    const serviceLife = SERVICE_LIFE_FIELD in values ? values[SERVICE_LIFE_FIELD] : getFieldValue(SERVICE_LIFE_FIELD);
    return {
        ...Object.fromEntries(Object.entries(values).map(([field, fieldValue]) => [field, fieldValue ?? ""])),
        [USEFUL_LIFE_REMAINING_FIELD]: getUsefullLifeRemaining(installedYear, serviceLife),
        [END_OF_SERVICE_LIFE_FIELD]: getEndOfServiceLife(installedYear, serviceLife),
        [CURRENT_AGE_FIELD]: getCurrentAge(installedYear)
    };
};

export const isEmptyFieldValue = value => value === "" || value === null || value === undefined;

export const getRecommendationLifecycleFieldKeys = isAssetType => {
    const prefix = isAssetType ? "assets." : "";
    return {
        serviceLife: `${prefix}service_life`,
        installedYear: `${prefix}installed_year`,
        usefulLifeRemaining: `${prefix}usefull_life_remaining`,
        endOfServiceLife: `${prefix}end_of_service_life`
    };
};

export const getSubsystemServiceLifeChangeSummary = ({ subSystem, serviceLifeKey, currentServiceLife }) => {
    if (!subSystem) return null;
    const newServiceLife = subSystem?.service_life ?? "";
    const current = currentServiceLife ?? "";
    if (String(newServiceLife) === String(current)) return null;
    return {
        values: { [serviceLifeKey]: newServiceLife },
        changes: [{ field: serviceLifeKey, label: "Service Life", from: current, to: newServiceLife }],
        serviceLifeChanged: true,
        newServiceLife,
        hasOnlyEmptyValues: isEmptyFieldValue(newServiceLife),
        serviceLifeKey,
        sourceField: "sub_system_id",
        sourceLabel: "Sub System",
        entityLabel: "Recommendation"
    };
};